import { isEmpty } from 'lodash-es';
import { nextTick } from 'vue';

import type { IContextMutationResult } from '@modules/core/services/query/types';
import type { IAffiliateProfileForm } from '@modules/profile-settings/types';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  ADMIN_PROFILE_SETTINGS_QUERY_KEY,
  AFFILIATE_PROFILE_SETTINGS_QUERY_KEY,
  PROFILE_SETTINGS_PERMISSIONS,
} from '@modules/profile-settings/constants';
import { RESPONSE_DATE_FORMAT } from '@modules/smart-filters/constants';
import { useToastStore } from '@modules/toast/store';
import { isDate } from '@utils/guards';
import { formatDate } from '@vueuse/core';

import type {
  IProfileSettingsGeneralResponse,
  IProfileSettingsGeneralSchema,
} from '../types';
import {
  ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_URL,
  AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_URL,
  PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_MUTATION_KEY,
} from '../constants';

export default function useProfileSettingsMainSaveMutation(): IContextMutationResult<
  IProfileSettingsGeneralResponse,
  Error,
  IProfileSettingsGeneralSchema
> {
  const { fetchPut } = useRestClient();
  const { displayPresetToast } = useToastStore();
  const { isAdminNamespace } = useCurrentNamespace();

  return useContextMutation<IProfileSettingsGeneralResponse, Error, IProfileSettingsGeneralSchema>({
    mutationKey: [PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_MUTATION_KEY],
    mutationFn: (payloadRaw) => {
      let payload;
      if (isAdminNamespace.value) {
        payload = { profile: payloadRaw };
      } else {
        const birthdayT = (payloadRaw as IAffiliateProfileForm)?.birthday;
        payload = {
          profile: {
            ...payloadRaw,
            birthday: isDate(birthdayT) ? formatDate(birthdayT, RESPONSE_DATE_FORMAT) : undefined,
          },
        };
      }
      return fetchPut<IProfileSettingsGeneralResponse>(
        isAdminNamespace.value ? ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_URL : AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_SAVE_URL,
        payload,
        {
          permission: PROFILE_SETTINGS_PERMISSIONS.EDIT,
        },
      );
    },
    async onSuccess({ queryClient, data, variables }) {
      if (isEmpty(data?.profile)) {
        if (isAdminNamespace.value) {
          void queryClient.invalidateQueries({ queryKey: [ADMIN_PROFILE_SETTINGS_QUERY_KEY] });
        } else {
          void queryClient.invalidateQueries({ queryKey: [AFFILIATE_PROFILE_SETTINGS_QUERY_KEY] });
        }
      } else {
        const queryKey = isAdminNamespace.value ? ADMIN_PROFILE_SETTINGS_QUERY_KEY : AFFILIATE_PROFILE_SETTINGS_QUERY_KEY;

        // Server response can be the same as previous one and vue does not update form, so first of all set payload as query data
        queryClient.setQueryData<IProfileSettingsGeneralResponse>([queryKey], (queryData) => {
          if (!queryData) {
            return queryData;
          }
          return {
            ...queryData,
            profile: {
              ...variables,
            },
          };
        });
        await nextTick();
        // Then set server response to trigger vue reactivity
        queryClient.setQueryData<IProfileSettingsGeneralResponse>([queryKey], (queryData) => {
          if (!queryData) {
            return queryData;
          }
          return {
            ...queryData,
            profile: {
              ...data?.profile,
            },
          };
        });
      }

      displayPresetToast('edited');
    },
  });
}
